import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Landmark, Info, AlertTriangle, ExternalLink, Power, KeyRound, ListChecks, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { toLocalYmd } from '@/lib/dateRange';

export default function HMRCMTDPage() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    client_id: '', client_secret: '', vrn: '',
    redirect_uri: '', environment: 'sandbox',
  });
  const [editMode, setEditMode] = useState(false);
  const [authState, setAuthState] = useState({ url: '', state: '', code: '' });
  const [obligations, setObligations] = useState([]);
  const [obligationsLoading, setObligationsLoading] = useState(false);
  // iter922 — Preview Boxes 1-9 state (Sandbox First-Filing).
  const [previewBoxes, setPreviewBoxes] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // iter923 — Sandbox Dry-Run + Diff state.
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [diffResult, setDiffResult] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/hmrc-mtd/config');
      setConfig(r.data);
      if (!r.data.configured) setEditMode(true);
      else {
        setForm((f) => ({
          ...f,
          vrn: r.data.vrn || '',
          redirect_uri: r.data.redirect_uri || '',
          environment: r.data.environment || 'sandbox',
        }));
      }
    } catch { toast.error('Failed to load HMRC config'); }
  };

  useEffect(() => { load(); }, []);

  // Listen for postMessage from /api/hmrc-mtd/callback (auto-exchanges authorization code)
  useEffect(() => {
    const handler = (ev) => {
      const d = ev.data;
      if (!d || d.source !== 'hmrc-mtd-callback' || !d.code) return;
      setAuthState((s) => ({ url: s.url, state: d.state || s.state, code: d.code }));
      // Auto-exchange immediately
      api.post('/hmrc-mtd/exchange-code', { code: d.code, state: d.state })
        .then((r) => {
          toast.success('Authorized — token valid until ' + (r.data.expires_at || '').slice(0, 16).replace('T', ' '));
          setAuthState({ url: '', state: '', code: '' });
          load();
        })
        .catch((e) => toast.error(e.response?.data?.detail || 'Auto-exchange failed'));
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const save = async () => {
    if (!form.client_id || !form.client_secret || !form.vrn || !form.redirect_uri) {
      toast.error('client_id, client_secret, VRN and redirect_uri are all required');
      return;
    }
    try {
      await api.put('/hmrc-mtd/config', form);
      toast.success('HMRC MTD credentials saved');
      setEditMode(false);
      setForm({ ...form, client_secret: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const disable = async () => {
    if (!window.confirm('Disable HMRC MTD live submission? Manual JSON download via /tax-submissions remains available.')) return;
    try {
      await api.delete('/hmrc-mtd/config');
      toast.success('HMRC MTD disabled');
      setObligations([]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startAuthorize = async () => {
    try {
      const r = await api.get('/hmrc-mtd/authorize-url');
      setAuthState({ url: r.data.url, state: r.data.state, code: '' });
      window.open(r.data.url, '_blank', 'noopener');
      toast.message('Authorize in the new tab, then paste the `code` query parameter back here.');
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || 'Configure credentials first');
    }
  };

  const exchangeCode = async () => {
    if (!authState.code) { toast.error('Paste the authorization code'); return; }
    try {
      const r = await api.post('/hmrc-mtd/exchange-code', { code: authState.code, state: authState.state });
      toast.success('Authorized — token valid until ' + (r.data.expires_at || '').slice(0, 16).replace('T', ' '));
      setAuthState({ url: '', state: '', code: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Exchange failed');
    }
  };

  const loadObligations = async () => {
    setObligationsLoading(true);
    try {
      const today = new Date();
      const yearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
      const r = await api.get('/hmrc-mtd/obligations', { params: {
        from_date: toLocalYmd(yearAgo),
        to_date: toLocalYmd(today),
      }});
      setObligations(r.data?.obligations || []);
      toast.success(`Loaded ${(r.data?.obligations || []).length} obligations`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not fetch obligations');
    } finally { setObligationsLoading(false); }
  };

  const isAuthorized = config?.authorized;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto" data-testid="hmrc-mtd-page">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-700 to-indigo-800 flex items-center justify-center shadow-lg">
            <Landmark size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">HMRC MTD (UK)</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Live OAuth + VAT return submission to HMRC Making Tax Digital REST API
            </p>
          </div>
        </div>

        {/* Status banner */}
        <Card className={isAuthorized
          ? 'border-emerald-300 bg-emerald-50/50 dark:bg-emerald-900/10'
          : config?.configured
            ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/10'
            : 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/10'}>
          <CardContent className="pt-4 flex items-start gap-3">
            {isAuthorized ? <CheckCircle2 size={22} className="text-emerald-600 shrink-0" />
              : <AlertTriangle size={22} className="text-amber-600 shrink-0" />}
            <div className="flex-1">
              <p className="font-semibold text-foreground" data-testid="hmrc-status-label">
                {isAuthorized
                  ? `Authorized (${config?.environment || 'sandbox'})`
                  : config?.configured
                    ? `Credentials saved — needs OAuth authorization (${config.environment || 'sandbox'})`
                    : 'Not Configured — Manual JSON download still works'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isAuthorized
                  ? `Token valid until ${(config?.token_expires_at || '').slice(0, 16).replace('T', ' ')} · scope ${config?.scope || ''}`
                  : config?.configured
                    ? 'Click "Authorize via HMRC" below to grant consent. Refresh tokens are auto-managed once authorized.'
                    : 'HMRC MTD live submission is OPTIONAL. You can keep using /tax-submissions for manual JSON download until you register your client credentials.'}
              </p>
              {config?.updated_at && (
                <p className="text-[11px] text-muted-foreground mt-1 font-mono">
                  Last updated: {(config.updated_at || '').slice(0, 16).replace('T', ' ')} by {config.updated_by || ''}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {config?.configured && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="hmrc-edit-btn">
                    Update credentials
                  </Button>
                  <Button variant="outline" size="sm" onClick={disable} className="text-rose-600 border-rose-200" data-testid="hmrc-disable-btn">
                    <Power size={12} className="mr-1" /> Disable
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Info size={14}/> How to register</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <ol className="list-decimal ml-5 space-y-1 text-muted-foreground">
              <li>Sign in to <a href="https://developer.service.hmrc.gov.uk" target="_blank" rel="noreferrer" className="text-info hover:underline inline-flex items-center gap-1">HMRC Developer Hub <ExternalLink size={11}/></a> and create an application.</li>
              <li>Subscribe to the <strong>VAT (MTD)</strong> API and any sandbox add-ons you need.</li>
              <li>Copy <strong>client_id</strong>, <strong>client_secret</strong>, your <strong>VRN</strong>, and the redirect URI registered on the portal — paste them below.</li>
              <li>Click <strong>Authorize via HMRC</strong>, sign in with your test or production gateway credentials, then paste the returned <code>code</code> back here.</li>
              <li>Once authorized, prepare a return on <a href="/tax-submissions" className="text-info hover:underline">Tax Submissions</a> and click <strong>Submit to HMRC</strong> to fire the live REST call.</li>
            </ol>
          </CardContent>
        </Card>

        {/* Credentials form */}
        {(editMode || !config?.configured) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><KeyRound size={14}/> Client Credentials</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Environment</Label>
                  <Select value={form.environment} onValueChange={(v) => setForm({ ...form, environment: v })}>
                    <SelectTrigger data-testid="hmrc-env"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sandbox">Sandbox (testing)</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">VAT Registration Number (VRN) *</Label>
                  <Input value={form.vrn} onChange={(e) => setForm({ ...form, vrn: e.target.value })} placeholder="123456789" data-testid="hmrc-vrn" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Client ID *</Label>
                <Input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="ABCD1234..." data-testid="hmrc-client-id" />
              </div>
              <div>
                <Label className="text-xs">Client Secret *</Label>
                <Input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} placeholder="secret-xxxxxxxx" data-testid="hmrc-client-secret" />
                <p className="text-[11px] text-muted-foreground mt-1">Stored encrypted, never displayed back. Leave empty when only updating non-secret fields.</p>
              </div>
              <div>
                <Label className="text-xs">Redirect URI *</Label>
                <Input value={form.redirect_uri} onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })} placeholder="https://your-app.com/hmrc-callback" data-testid="hmrc-redirect-uri" />
                <p className="text-[11px] text-muted-foreground mt-1">Must exactly match the redirect URI registered on HMRC Developer Hub.</p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                {config?.configured && (
                  <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                )}
                <Button onClick={save} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="hmrc-save-btn">
                  {config?.configured ? 'Update' : 'Save Credentials'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* OAuth flow (visible after credentials saved) */}
        {config?.configured && !editMode && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><KeyRound size={14}/> OAuth Authorization</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={startAuthorize} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="hmrc-authorize-btn">
                  <ExternalLink size={14} className="mr-1" /> Authorize via HMRC
                </Button>
                {isAuthorized && (
                  <span className="text-xs text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1">
                    <CheckCircle2 size={12} /> Currently authorized
                  </span>
                )}
              </div>
              {authState.state && (
                <div className="p-3 rounded-md border border-border bg-muted/30 space-y-2">
                  <p className="text-xs font-medium text-foreground">Paste the authorization code returned by HMRC</p>
                  <p className="text-[11px] text-muted-foreground break-all">
                    After signing in, HMRC redirects to your <code>redirect_uri</code> with a <code>?code=...</code> parameter.
                    Copy the <code>code</code> value from the URL bar and paste below.
                  </p>
                  <div className="flex gap-2">
                    <Input value={authState.code}
                      onChange={(e) => setAuthState({ ...authState, code: e.target.value })}
                      placeholder="abc123..." data-testid="hmrc-code-input" />
                    <Button onClick={exchangeCode} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="hmrc-exchange-btn">
                      Exchange code
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground font-mono">state: {authState.state}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* iter922 — Preview 9 boxes from ledger BEFORE submitting */}
        {isAuthorized && (
          <Card className="border-2 border-emerald-200 dark:border-emerald-900/50" data-testid="hmrc-preview-boxes-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-base">Preview 9 Boxes — from your ledger</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Pulls the current VAT period totals straight from Sales / Purchases / Journal entries so you can eyeball them before submitting.</p>
              </div>
              <Button
                size="sm"
                onClick={async () => {
                  setPreviewLoading(true);
                  try {
                    const { data } = await api.get('/hmrc-mtd/preview-boxes');
                    setPreviewBoxes(data);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Preview failed');
                    setPreviewBoxes(null);
                  } finally {
                    setPreviewLoading(false);
                  }
                }}
                disabled={previewLoading}
                data-testid="hmrc-preview-boxes-refresh"
              >
                {previewLoading ? 'Loading…' : (previewBoxes ? 'Refresh' : 'Preview from ledger')}
              </Button>
            </CardHeader>
            <CardContent>
              {!previewBoxes ? (
                <div className="text-center text-xs text-muted-foreground py-6 rounded-lg bg-muted/40 border border-dashed">
                  Click <b>Preview from ledger</b> to compute boxes 1-9 from your accounting data. Nothing is sent to HMRC — this is a local preview so you can spot errors before submission.
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-[11px] text-muted-foreground flex flex-wrap gap-3">
                    <span><b>Period:</b> {previewBoxes.period?.start} → {previewBoxes.period?.end}</span>
                    {previewBoxes.vrn && <span><b>VRN:</b> {previewBoxes.vrn}</span>}
                    <span><b>Env:</b> {previewBoxes.environment}</span>
                  </div>
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                        <tr>
                          <th className="text-left px-4 py-2 w-12">Box</th>
                          <th className="text-left px-4 py-2">Description</th>
                          <th className="text-right px-4 py-2 w-32">Amount (GBP)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(previewBoxes.boxes || []).map((b) => (
                          <tr key={b.key} className="border-t hover:bg-muted/20" data-testid={`hmrc-preview-box-${b.box}`}>
                            <td className="px-4 py-2 font-bold text-emerald-700">{b.box}</td>
                            <td className="px-4 py-2 text-xs">{b.label.replace(/^\d+\.\s*/, '')}</td>
                            <td className="px-4 py-2 text-right font-mono font-semibold" data-testid={`hmrc-preview-amount-${b.box}`}>
                              £ {Number(b.amount).toLocaleString(undefined, { minimumFractionDigits: b.box >= '6' ? 0 : 2, maximumFractionDigits: b.box >= '6' ? 0 : 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {Array.isArray(previewBoxes.warnings) && previewBoxes.warnings.length > 0 && (
                    <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-2 text-xs text-amber-800 dark:text-amber-200" data-testid="hmrc-preview-warnings">
                      <b>Ledger warnings:</b>
                      <ul className="list-disc ml-4 mt-1">
                        {previewBoxes.warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground italic pt-1">
                    Boxes 6-9 are shown as whole pounds (HMRC requirement). Boxes 1-5 include pence.
                    Values are computed from accounting entries in this period — verify them against your general ledger before submitting.
                  </p>
                  {/* iter923 — Sandbox Dry-Run + Diff. Fires the preview
                       payload against HMRC's TEST endpoint (never
                       production) and then computes a per-box diff so
                       operators catch rounding drift before flipping to
                       real submission. */}
                  <div className="pt-3 border-t space-y-2 mt-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="text-sm font-semibold flex items-center gap-2">🧪 Sandbox Dry-Run</div>
                        <p className="text-[11px] text-muted-foreground">Fire this preview against HMRC&apos;s test-api endpoint. Never touches production.</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={dryRunLoading || !previewBoxes?.payload}
                          data-testid="hmrc-dryrun-btn"
                          onClick={async () => {
                            setDryRunLoading(true);
                            try {
                              const { data } = await api.post('/hmrc-mtd/dry-run', { payload: previewBoxes.payload });
                              setDryRunResult(data);
                              if (data.ok) toast.success(`Dry-run OK — HMRC processingDate: ${data.receipt?.processingDate || 'n/a'}`);
                              else toast.error(`HMRC ${data.http_status}: ${JSON.stringify(data.response).slice(0,180)}`);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Dry-run failed');
                            } finally { setDryRunLoading(false); }
                          }}
                        >
                          {dryRunLoading ? 'Submitting to sandbox…' : 'Submit to Sandbox (dry-run)'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={diffLoading}
                          data-testid="hmrc-diff-btn"
                          onClick={async () => {
                            setDiffLoading(true);
                            try {
                              const { data } = await api.get('/hmrc-mtd/diff');
                              setDiffResult(data);
                              if (data.all_match) toast.success('Preview matches HMRC exactly ✓');
                              else toast.warning('Drift detected — see diff below');
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Diff failed');
                              setDiffResult(null);
                            } finally { setDiffLoading(false); }
                          }}
                        >
                          {diffLoading ? 'Diffing…' : 'Preview-vs-HMRC diff'}
                        </Button>
                      </div>
                    </div>
                    {dryRunResult && (
                      <div className={`rounded-lg border p-2 text-xs ${dryRunResult.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-testid="hmrc-dryrun-result">
                        <div className="font-semibold">{dryRunResult.ok ? '✓ HMRC sandbox accepted the payload' : `✗ HMRC ${dryRunResult.http_status}`}</div>
                        {dryRunResult.receipt?.processingDate && <div>processingDate: <code>{dryRunResult.receipt.processingDate}</code></div>}
                        {dryRunResult.receipt?.formBundleNumber && <div>formBundleNumber: <code>{dryRunResult.receipt.formBundleNumber}</code></div>}
                        {!dryRunResult.ok && <pre className="text-[10px] whitespace-pre-wrap mt-1">{JSON.stringify(dryRunResult.response, null, 2)}</pre>}
                      </div>
                    )}
                    {diffResult && (
                      <div className={`rounded-lg border p-2 space-y-1 ${diffResult.all_match ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="hmrc-diff-result">
                        <div className="text-xs font-semibold flex items-center justify-between">
                          <span>{diffResult.all_match ? '✓ Preview matches sent payload exactly' : '⚠ Drift detected'}</span>
                          <span className="text-muted-foreground text-[10px]">dry-run {diffResult.dry_run_id?.slice(0, 8)} · {diffResult.environment}</span>
                        </div>
                        {/* iter924 — Submit-to-HMRC guardrail banner.
                             When the diff shows any non-zero deltas, the
                             backend will REJECT any /hmrc-mtd/submit call
                             with 428 Precondition Required. This banner
                             surfaces the reason so the operator knows to
                             re-run the dry-run rather than getting a
                             cryptic error later. */}
                        {!diffResult.all_match && (
                          <div className="rounded-md bg-rose-100 border border-rose-300 dark:bg-rose-950/40 dark:border-rose-800 p-2 text-[11px]" data-testid="hmrc-submit-blocked">
                            <p className="font-semibold text-rose-800 dark:text-rose-200">🚫 Live submission blocked</p>
                            <p className="text-rose-700 dark:text-rose-300 mt-0.5">
                              At least one box differs from what you dry-ran. Fix the ledger or re-run the dry-run + diff before hitting <b>Submit to HMRC</b>. To override, pass <code>?force=true</code> to the submit call.
                            </p>
                          </div>
                        )}
                        <table className="w-full text-[11px]">
                          <thead className="text-muted-foreground uppercase tracking-wider">
                            <tr>
                              <th className="text-left py-0.5">Box</th>
                              <th className="text-right py-0.5">Preview (now)</th>
                              <th className="text-right py-0.5">Sent (dry-run)</th>
                              <th className="text-right py-0.5">Δ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(diffResult.diffs || []).map((d) => (
                              <tr key={d.key} className={d.match ? '' : 'bg-rose-100/60 dark:bg-rose-950/30 font-semibold'} data-testid={`hmrc-diff-row-${d.key}`}>
                                <td className="py-0.5 pr-2">{d.key}</td>
                                <td className="py-0.5 pr-2 text-right font-mono">£{d.preview.toLocaleString()}</td>
                                <td className="py-0.5 pr-2 text-right font-mono">£{d.sent.toLocaleString()}</td>
                                <td className={`py-0.5 text-right font-mono ${d.match ? 'text-muted-foreground' : 'text-rose-700'}`}>
                                  {d.delta === 0 ? '—' : (d.delta > 0 ? '+' : '') + d.delta.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Obligations */}
        {isAuthorized && (
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><ListChecks size={14}/> Open VAT obligations</CardTitle>
              <Button size="sm" variant="outline" onClick={loadObligations} disabled={obligationsLoading} data-testid="hmrc-load-obligations">
                {obligationsLoading ? 'Loading...' : 'Fetch from HMRC'}
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-2">Period</th>
                      <th className="text-left px-4 py-2">Period Key</th>
                      <th className="text-left px-4 py-2">Due</th>
                      <th className="text-left px-4 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {obligations.length === 0 ? (
                      <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">No obligations loaded yet. Click "Fetch from HMRC" above.</td></tr>
                    ) : obligations.map((o, i) => (
                      <tr key={i} className="border-b border-border">
                        <td className="px-4 py-2 font-mono text-xs">{o.start} → {o.end}</td>
                        <td className="px-4 py-2 font-mono text-xs">{o.periodKey}</td>
                        <td className="px-4 py-2 font-mono text-xs">{o.due}</td>
                        <td className="px-4 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${o.status === 'F' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {o.status === 'F' ? 'Fulfilled' : 'Open'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
